import { InstantWinStorage } from '@/services/instant-win-storage'
import type {
  CheckoutContext,
  GameSessionData,
  InstantWinDraw,
  Plan,
  ReservationData,
} from '@/types'
import { InstantWinType, ProductTypes } from '@/utils/helper-objects'
import { isE164PhoneNumber } from '@/utils/input-validator-utils'
import { DateTime } from 'luxon'
import type { Router } from 'vue-router'

/**
 * Determine if this is a credit-only payment (no card required)
 */
export const isCreditOnlyPayment = (
  useCredit: boolean,
  paymentDue: number,
  checkoutContext: CheckoutContext
): boolean => {
  return useCredit && paymentDue === 0 && hasInstantWin(checkoutContext)
}

/**
 * Calculate the chargeable amount from basket
 */
export const calculateChargeableAmount = (
  checkoutContext: CheckoutContext
): number => {
  return Number((checkoutContext.basket.totalPrice / 100).toFixed(2))
}

/**
 * Calculate the due amount from basket
 */
export const calculateDueAmount = (
  checkoutContext: CheckoutContext,
  chargeableAmount: number,
  usedCredit: number
): number => {
  if (hasSubscription(checkoutContext)) {
    const subscriptionItems = checkoutContext.basket.items.filter(
      (item) => item.type === ProductTypes.Subscription
    )

    if (subscriptionItems.length > 0) {
      const plan = subscriptionItems[0].product as Plan
      if (plan.temp_discount === true && plan.discount_cost) {
        // Use plan.discount_cost for temp_discount plans instead of free trial cost
        return Number(plan.discount_cost.toFixed(2))
      }
    }
  }

  return chargeableAmount - usedCredit
}

/**
 * Calculate used credit amount
 */
export const calculateUsedCredit = (
  useCredit: boolean,
  chargeableAmount: number,
  availableUserCredit: number
): number => {
  if (!useCredit) {
    return 0
  }
  return chargeableAmount > availableUserCredit
    ? availableUserCredit
    : chargeableAmount
}

/**
 * Handle navigation after successful payment
 */
export const navigateAfterPayment = (
  paymentResult: any,
  checkoutContext: CheckoutContext,
  router: Router,
  gameSessionData?: GameSessionData,
  refreshCreditBalance?: () => void
): void => {
  if (hasSubscription(checkoutContext)) {
    setTimeout(() => {
      refreshCreditBalance?.()
    }, 3000)
    handleSubscriptionNavigation(paymentResult, router)
  } else if (hasInstantWin(checkoutContext)) {
    handleInstantWinNavigation(
      paymentResult,
      checkoutContext,
      router,
      gameSessionData
    )
  }
}

/**
 * Check if checkout context has subscription products
 */
export const hasSubscription = (checkoutContext: CheckoutContext): boolean => {
  return (
    checkoutContext.basket?.items?.some(
      (item) => item.type === ProductTypes.Subscription
    ) ?? false
  )
}

/**
 * Check if checkout context has instant win products
 */
export const hasInstantWin = (checkoutContext: CheckoutContext): boolean => {
  return (
    checkoutContext.basket?.items?.some(
      (item) => item.type === ProductTypes.InstantWinDraw
    ) ?? false
  )
}

/**
 * Handle subscription payment navigation
 */
export const handleSubscriptionNavigation = (
  paymentResult: any,
  router: Router
): void => {
  if (
    paymentResult.redirect?.route_name &&
    paymentResult.redirect?.query_params
  ) {
    // Use backend redirect data with all required parameters
    void router.push({
      name: paymentResult.redirect.route_name,
      query: paymentResult.redirect.query_params,
    })
  } else {
    // Fallback to members-zone since thank-you page needs specific backend parameters
    // that we don't have available in the fallback scenario
    console.warn('Missing backend redirect data, redirecting to members-zone')
    void router.push({ name: 'members-zone' })
  }
}

/**
 * Handle instant win payment navigation
 */
export const handleInstantWinNavigation = (
  _paymentResult: any,
  checkoutContext: CheckoutContext,
  router: Router,
  gameSessionData?: GameSessionData
): void => {
  if (!gameSessionData) {
    console.error('Game session data is required for instant win navigation')
    return
  }

  const instantWinItems = checkoutContext.basket.items.filter(
    (item) => item.type === ProductTypes.InstantWinDraw
  )
  const instantWin = instantWinItems[0]?.product as InstantWinDraw

  if (!instantWin) {
    return
  }

  // Handle Instant Pack and Mystery Box (and eventually, all V2) game navigation
  if (
    instantWin.instant_win_type_id === InstantWinType.InstantPack ||
    instantWin.instant_win_type_id === InstantWinType.InstantSpin ||
    instantWin.instant_win_type_id === InstantWinType.MysteryBox
  ) {
    void router.push({
      name: 'instant-wins-play',
      params: {
        instantWinCode: instantWin.code,
      },
      query: {
        gameSessionId: gameSessionData.game_session_id,
      },
    })
    return
  }

  // Navigate to confirmation page for other types (Classic)
  void router.push({
    name: 'instant-win-checkout-order-confirmation',
    params: {
      instantWinCode: instantWin.code,
    },
    query: {
      gameSessionId: gameSessionData.game_session_id,
    },
  })
}

/**
 * Process instant win ticket data and update user store
 */
export const processInstantWinTickets = async (
  paymentResult: any,
  checkoutContext: CheckoutContext,
  userStore: any
): Promise<{ success: boolean; error?: string }> => {
  const instantWinItems = checkoutContext.basket.items.filter(
    (item) => item.type === ProductTypes.InstantWinDraw
  )
  const instantWin = instantWinItems[0]?.product as InstantWinDraw

  if (!instantWin) {
    return { success: false, error: 'No instant win product found' }
  }

  const drawCode = instantWin.code
  let ticketData = paymentResult.data
  if (paymentResult.data && paymentResult.data.data) {
    ticketData = paymentResult.data.data
  }

  const ticketsData = ticketData[drawCode] && ticketData[drawCode][0]

  if (!ticketsData) {
    return {
      success: false,
      error:
        'Your payment was successful, but we could not retrieve your ticket information. Please contact customer support.',
    }
  }

  // Set the instant win ticket data
  userStore.setInstantWinTickets(instantWin, {
    draw_code: drawCode,
    purchased_at: DateTime.now(),
    is_winner: ticketsData.is_winner || false,
    winning_entries: ticketsData.winning_entries || [],
    non_winning_entries: ticketsData.non_winning_entries || [],
    purchase_id: ticketsData.purchase_id || paymentResult.orderId,
  })

  return { success: true }
}

/**
 * Process game session data from new backend response
 */
export const processGameSession = async (
  paymentResult: any,
  checkoutContext: CheckoutContext,
  userStore: any
): Promise<{
  success: boolean
  error?: string
  gameSessionData?: GameSessionData
}> => {
  if (!hasInstantWin(checkoutContext)) {
    return { success: false, error: 'No instant win product found' }
  }

  // Check for the game_session data
  if (!paymentResult.data?.game_session) {
    return {
      success: false,
      error:
        'Invalid payment response: missing game_session data. Please contact customer support.',
    }
  }

  const gameSessionData = paymentResult.data.game_session as GameSessionData

  // Validate required fields
  if (
    !gameSessionData.game_session_id ||
    !gameSessionData.total_plays ||
    !gameSessionData.draw_code
  ) {
    return {
      success: false,
      error:
        'Invalid game session data received. Please contact customer support.',
    }
  }

  // Still need to refresh user details for credit balance
  await userStore.fetchDetails(true)
  userStore.creditHistory.clear()

  return { success: true, gameSessionData }
}

/**
 * Validate payment form data
 */
export const validatePaymentForm = (
  isValid: boolean,
  isCreditOnlyPayment: boolean
): boolean => {
  // Always validate form unless it's a credit-only payment
  return isValid || isCreditOnlyPayment
}

/**
 * Create standardized error object
 */
export const createPaymentError = (
  message: string,
  reference?: string
): { message: string; reference?: string } => {
  const error: { message: string; reference?: string } = { message }
  if (reference) {
    error.reference = reference
  }
  return error
}

/**
 * Check if user should see address fields
 */
export const shouldShowAddressFields = (
  user: any,
  _paymentDue: number,
  checkoutContext: CheckoutContext,
  isFeatureEnabled: (flag: string) => boolean,
  isPostcodeValidator: any // Accept the validator as-is
): boolean => {
  // Check if postcode is valid using the validator
  const isPostcodeValid =
    isPostcodeValidator({
      name: 'post_code',
      value: String(user.post_code || ''),
    }) === true

  const hasIncompleteAddress =
    !user.address_line_1 || !user.city || !user.post_code || !isPostcodeValid

  if (!hasIncompleteAddress) {
    return false
  }

  if (
    hasSubscription(checkoutContext) &&
    isFeatureEnabled('solidgate_sub_address')
  ) {
    return true
  }

  if (
    hasInstantWin(checkoutContext) &&
    isFeatureEnabled('solidgate_iw_address')
  ) {
    return true
  }

  return false
}

/**
 * Check if user should see personal fields (first name, last name, phone)
 */
export const shouldShowPersonalFields = (user: any): boolean => {
  // Check if phone is valid using the validator
  const isPhoneValid = user.phone
    ? isE164PhoneNumber({
        name: 'phone',
        value: String(user.phone),
      }) === true
    : false

  // Show personal fields if any required field is missing or invalid
  return (
    !user.first_name ||
    !user.last_name ||
    !user.phone ||
    String(user.first_name).trim() === '' ||
    String(user.last_name).trim() === '' ||
    String(user.phone).trim() === '' ||
    !isPhoneValid
  )
}

/**
 * Check and create reservation for instant win products
 */
export const checkAndCreateReservation = async (
  checkoutContext: CheckoutContext
): Promise<{
  success: boolean
  reservationData?: ReservationData
  error?: string
}> => {
  try {
    // Handle instant win reservations
    const instantWinItems = checkoutContext.basket.items.filter(
      (item) => item.type === ProductTypes.InstantWinDraw
    )

    if (instantWinItems.length === 0) {
      return { success: true } // No instant win items, no reservation needed
    }

    const instantWin = instantWinItems[0].product as InstantWinDraw
    const quantity = instantWinItems[0].quantity

    const reservationData = await InstantWinStorage.checkOrCreateReservation(
      instantWin,
      quantity
    )

    return {
      success: true,
      reservationData,
    }
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to handle reservation',
    }
  }
}

/**
 * Handle reservation expiry
 */
export const handleReservationExpiry = (): {
  shouldRedirect: boolean
  redirectRoute: string
} => {
  // Redirect to instant wins page on reservation expiry
  return {
    shouldRedirect: true,
    redirectRoute: 'instant-wins',
  }
}

/**
 * Check if checkout context has instant win products that need reservations
 */
export const needsReservation = (checkoutContext: CheckoutContext): boolean => {
  return hasInstantWin(checkoutContext)
}
